import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AccountActions, EnvironmentActions } from '@app/core/actions';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import { CookiesService } from '@app/core/services/cookies.service';
import { IamService } from '@app/core/services/iam.service';
import { TokenService } from '@app/core/services/token.service';
import * as fromRoot from '@app/reducers';
import { getLanguageWithFallback } from '@app/shared/helpers/language';
import { getProtocolHostnameAndPort, isAppDomain } from '@app/shared/helpers/location';
import { addOrUpdateUrlParam } from '@app/shared/helpers/urlHelper';
import { genericRetry } from '@app/shared/operators/genericRetry';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Billing } from '@smash-sdk/billing/ejs/01-2024/billing';
import { Region, config } from '@smash-sdk/core';
import { Directory, errors } from '@smash-sdk/directory/ejs/11-2023';
import { Discovery } from '@smash-sdk/discovery/ejs/02-2023/discovery';
import { defer, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { encodeSamlRequest } from 'smash-saml';
import { Constant } from 'src/constant';
import { environment } from 'src/environments/environment';
import { SigninActions } from '../actions';
import * as fromSignin from '../reducers';
import { MfaActions } from '../actions';
import ListPublicServicesError from '@smash-sdk/discovery/ejs/02-2023/types/ListPublicServices/ListPublicServicesError';
import ListSubscriptionLimitsError from '@smash-sdk/billing/ejs/01-2024/types/ListSubscriptionLimits/ListSubscriptionLimitsError';

@Injectable()
export class SigninEffects {

    loadSigninConfiguration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SigninActions.loadSigninConfiguration),
            withLatestFrom(
                this.store$.pipe(select(fromRoot.selectServices))
            ),
            switchMap(([action, { services }]) => {
                const directorySdk = new Directory();
                return defer(() => directorySdk.getSigninConfiguration({ username: action.username })).pipe(
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: errors.GetSigninConfigurationError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: errors.GetSigninConfigurationError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                            { error: errors.GetSigninConfigurationError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    map(({ configuration }) => {
                        const { type, providers, region } = configuration;
                        if (type === Constant.signinMode.sso && providers.length > 0) {
                            const idpUrl = providers[0].idpUrl;
                            const audienceUris = providers[0].audienceUris;
                            const consumerServiceUrl = services.find(service => service.name === 'iam' && service.region === region).url + '/saml/signon';
                            const correspondingAudienceUri = isAppDomain() ? environment.appDomainWithProtocol : audienceUris.find(audienceUri => audienceUri === getProtocolHostnameAndPort());
                            const SAMLRequest = encodeSamlRequest({ consumerServiceUrl, audienceUri: correspondingAudienceUri });
                            window.location.href = addOrUpdateUrlParam(idpUrl, Constant.SAMLRequestKey, SAMLRequest);
                        }
                        return SigninActions.loadSigninConfigurationSuccess({ configuration });
                    }),
                    catchError((error: any) => {
                        if (error instanceof errors.GetSigninConfigurationError.NotFoundError) {
                            return [SigninActions.loadSigninConfigurationFailure({ error: 'SIGNIN_ALERT_MESSAGE_EMAIL_NOT_FOUND' })];
                        }
                        return [SigninActions.loadSigninConfigurationFailure({ error: 'SIGNIN_ALERT_MESSAGE_ERROR' })];
                    })
                )
            })
        )
    );

    automaticRedirectSso$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SigninActions.automaticRedirectSso),
            switchMap((action) => {
                try {
                    const { encodedSsoInformations } = action;
                    const decodedSsoInformations = JSON.parse(atob(decodeURIComponent(encodedSsoInformations)));
                    const { idpUrl, consumerServiceUrl, audienceUri } = decodedSsoInformations;
                    const SAMLRequest = encodeSamlRequest({ consumerServiceUrl, audienceUri });
                    window.location.href = addOrUpdateUrlParam(idpUrl, Constant.SAMLRequestKey, SAMLRequest);
                    return of(SigninActions.automaticRedirectSsoSuccess());
                } catch (error) {
                    return of(SigninActions.automaticRedirectSsoFailure({ error }));
                }
            })
        )
    );

    signinWithPassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SigninActions.signinWithPassword),
            withLatestFrom(
                this.store$.select(fromRoot.getSelectedLanguage),
            ),
            concatMap(([action, selectedLanguage]) => {
                const { username, password } = action;
                const directorySdk = new Directory();
                return defer(() => directorySdk.createToken({ username, password })).pipe(
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: errors.CreateTokenError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: errors.CreateTokenError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                            { error: errors.CreateTokenError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    concatMap(({ identity }) => {
                        this.cookiesService.setCookie('identity', identity);
                        config.setToken(identity.token.token);
                        this.gtmService.pushTag({
                            event: Constant.eventGTM.SIGNIN,
                            signin_method: Constant.signinMode.standard,
                        });

                        const userLanguage = identity?.language || selectedLanguage;
                        const languageWithFallback = getLanguageWithFallback(userLanguage);
                        const discoverySdk = new Discovery({ region: identity.region as Region }); // TO FIX Maybe find a better way to avoid forced typing Region
                        return defer(() => discoverySdk.listPublicServices()).pipe(
                            genericRetry({
                                retryConfig: [
                                    ...Constant.NetworkErrorsRetryConfiguration,
                                    { error: ListPublicServicesError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                                    { error: ListPublicServicesError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                                    { error: ListPublicServicesError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                                ]
                            }),
                            concatMap((resServices: any) => {
                                const { region, country, countryCode } = resServices;
                                const isRoot = this.tokenService.isRoot(identity.token.token);
                                const account = this.tokenService.getAccountFromToken(identity.token.token);
                                const computedIdentity = {
                                    ...identity,
                                    account,
                                    isRoot
                                };

                                return [
                                    AccountActions.selectActiveIdentity({ identity: computedIdentity }),
                                    EnvironmentActions.SelectLanguage({ language: languageWithFallback }),
                                    EnvironmentActions.loadServicesSuccess({ region, country, countryCode }),
                                    SigninActions.signinCheckAccountType({ identity: computedIdentity, region }),
                                ];
                            }));
                    }),
                    catchError((error: unknown) => {
                        if (error instanceof errors.CreateTokenError.NotFoundError
                            || error instanceof errors.CreateTokenError.InvalidAuthenticationModeError
                            || error instanceof errors.CreateTokenError.InvalidCredentialsError
                            || error instanceof errors.CreateTokenError.IdentityDisabledError
                            || error instanceof errors.CreateTokenError.PasswordRevokedError
                        ) {
                            return of(SigninActions.signinWithPasswordFailure({ error: 'SIGNIN_ALERT_MESSAGE_INVALID_PASSWORD' }));
                        } else if (error instanceof errors.CreateTokenError.MfaRequiredError) {
                            const { details: { token, mfa, region } }: any = error;
                            const discoverySdk = new Discovery({ region });
                            return defer(() => discoverySdk.listPublicServices()).pipe(
                                genericRetry({
                                    retryConfig: [
                                        ...Constant.NetworkErrorsRetryConfiguration,
                                        { error: ListPublicServicesError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                                        { error: ListPublicServicesError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                                        { error: ListPublicServicesError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                                    ]
                                }),
                                concatMap((resServices: any) => {
                                    const { region, country, countryCode } = resServices;
                                    return [
                                        MfaActions.DisplayMfaChallenge({ token, mfa, region }),
                                        EnvironmentActions.loadServicesSuccess({ region, country, countryCode }),
                                    ];
                                }));
                        } else {
                            return of(SigninActions.signinWithPasswordFailure({ error: 'SIGNIN_ALERT_MESSAGE_ERROR' }));
                        }
                    })
                );
            })
        )
    );

    signinCheckAccountType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SigninActions.signinCheckAccountType),
            concatMap((action) => {
                const { identity, region } = action
                const billingSdk = new Billing();
                return defer(() => billingSdk.listSubscriptionLimits()).pipe(
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: ListSubscriptionLimitsError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: ListSubscriptionLimitsError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                            { error: ListSubscriptionLimitsError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    concatMap((resLimits) => {
                        const { limits: { services: { transfer: { dataInOut } } } } = resLimits;
                        const accountType = dataInOut && dataInOut.enabled ? Constant.accountTypes.developer : Constant.accountTypes.premium;
                        return [SigninActions.signinCheckAccountTypeSuccess({ identity, region, accountType })];
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error instanceof ListSubscriptionLimitsError.ForbiddenError) { // wait for framework update
                            // TO FIX !!! to remove when most of account update their policies
                            const { identity, region } = action
                            return [SigninActions.signinCheckAccountTypeSuccess({ identity, region, accountType: Constant.accountTypes.premium })];
                        } else {
                            return of(SigninActions.signinWithPasswordFailure({ error: 'SIGNIN_ALERT_MESSAGE_ERROR' }));
                        }
                    })
                );
            })
        )
    );

    signinCheckAccountTypeSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SigninActions.signinCheckAccountTypeSuccess),
            concatMap((action) => {
                const { identity, region, accountType } = action;
                if (accountType === Constant.accountTypes.developer) {
                    return this.iamService.createAccount().pipe(
                        concatMap(({ account }: any) => {
                            this.cookieService.setCookie('identity', account);
                            config.setToken(account.token.token);
                            return [
                                AccountActions.createUserAnonymousSuccess({ identity: account }),
                                AccountActions.selectActiveIdentity({ identity: account }),
                                SigninActions.signinWithPasswordFailure({ error: 'SIGNIN_ALERT_MESSAGE_INVALID_ACCOUNT' }),
                            ];
                        }),
                        catchError((error: any) =>
                            of(AccountActions.createUserAnonymousFailure({ error: error.message }))
                        )
                    );
                } else {
                    return [
                        SigninActions.signinWithPasswordSuccess({ identity, region }),
                        SigninActions.signinSuccess({ identity, region }),
                    ];
                }
            })
        )
    );

    signinWithPasswordSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SigninActions.signinWithPasswordSuccess),
            tap(() => {
                this.router.navigateByUrl('/');
            })
        ),
        { dispatch: false }
    );

    constructor(
        private actions$: Actions,
        private router: Router,
        private tokenService: TokenService,
        private iamService: IamService,
        private cookieService: CookiesService,
        private cookiesService: CookiesService,
        private gtmService: GoogleTagManagerService,
        private store$: Store<fromRoot.State & fromSignin.State>
    ) { }

}
